import { useState } from "react";
import { X, MessageSquare, Hash } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { useAuth } from "../../../../context/AuthContext";
import { PreguntaAsambleaRecord } from "../../../../api/preguntaAsambleaService";
import { insertarRespuestaPreguntaAsamblea } from "../../../../api/respuestaPreguntaAsambleaService";
import Swal from "sweetalert2";

interface CreateRespuestaPreguntaAsambleaModalProps {
  isOpen: boolean;
  pregunta: PreguntaAsambleaRecord | null;
  onClose: (created: boolean) => void;
}

export default function CreateRespuestaPreguntaAsambleaModal({
  isOpen,
  pregunta,
  onClose,
}: CreateRespuestaPreguntaAsambleaModalProps) {
  const { session } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [orden, setOrden] = useState("");
  const [texto, setTexto] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!orden.trim()) {
      await Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor ingrese la nomenclatura (letra o número)",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    if (!texto.trim()) {
      await Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor ingrese el texto de la respuesta",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const usuarioAdministrador = session?.email || "";
      const preguntaCodigo = String(pregunta?.pra_codigo || "");

      if (!usuarioAdministrador || !preguntaCodigo) {
        throw new Error("No se pudo obtener la información necesaria");
      }

      const payload = {
        usuario_administrador: usuarioAdministrador,
        pregunta_codigo: preguntaCodigo,
        texto: texto.trim(),
        orden: orden.trim(),
      };

      const response = await insertarRespuestaPreguntaAsamblea(payload);

      if (response.code === "1" || response.code === 1) {
        await Swal.fire({
          icon: "success",
          title: "¡Respuesta creada!",
          text: response.message || "La respuesta se ha creado exitosamente",
          confirmButtonColor: "#3d5a40",
          timer: 2000,
          timerProgressBar: true,
          didOpen: (popup) => {
            (popup as HTMLElement).style.zIndex = '10001';
          },
        });
        setOrden("");
        setTexto("");
        onClose(true);
      } else {
        throw new Error(response.message || "Error al crear la respuesta");
      }
    } catch (error) {
      console.error("Error al crear respuesta:", error);
      const errorMessage = error instanceof Error ? error.message : "Error al crear la respuesta";
      const mensajeTraducido = errorMessage.includes("Request failed")
        ? "Error al crear la respuesta. Por favor, intente nuevamente."
        : errorMessage.includes("Network Error")
        ? "Error de conexión. Verifique su conexión a internet."
        : errorMessage;

      await Swal.fire({
        icon: "error",
        title: "Error",
        text: mensajeTraducido,
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !pregunta) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="bg-white border-b px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-military-600 flex items-center gap-2">
              <MessageSquare size={28} />
              Crear Nueva Respuesta
            </h2>
            <p className="text-base text-gray-600 mt-1">
              Pregunta: <span className="font-bold text-lg">{pregunta.pra_texto}</span>
            </p>
          </div>
          <button
            onClick={() => onClose(false)}
            className="text-gray-400 hover:text-gray-600 transition"
            disabled={isSubmitting}
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto flex-1">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Hash size={16} className="inline mr-1" />
              Nomenclatura (Letra o Número) <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              value={orden}
              onChange={(e) => setOrden(e.target.value)}
              placeholder="Ej: 1, 2, 3, A, B, C..."
              disabled={isSubmitting}
              required
              maxLength={10}
            />
            <p className="text-xs text-gray-500 mt-1">
              Puede ser una letra o número (Ej: 1, 2, 3, A, B, C, etc.)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MessageSquare size={16} className="inline mr-1" />
              Texto de la respuesta <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Escribe el texto de la respuesta..."
              disabled={isSubmitting}
              className="min-h-[120px]"
              required
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t">
            <Button
              type="button"
              onClick={() => onClose(false)}
              variant="outline"
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-military-500 hover:bg-military-600 text-white"
            >
              {isSubmitting ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                  Guardando...
                </>
              ) : (
                "Guardar Respuesta"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
